// src/lib/utils/location.ts

// Default fallback (Valencia, Spain)
export const DEFAULT_LOCATION = {
  latitude: 39.4699,
  longitude: -0.3763
};

const LOCATION_CACHE_DURATION   = 5 * 60 * 1000; // 5m
const LOCATION_CACHE_KEY        = 'user_location_cache';
const LOCATION_ONBOARD_KEY      = 'user_location';
const LOCATION_PERMISSION_KEY   = 'locationPermission';

interface CachedLocation {
  latitude: number;
  longitude: number;
  timestamp: number;
}

/** Safely read from localStorage (never throws) */
function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Safely write to localStorage (never throws) */
function safeSetItem(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore failures (e.g. Private Mode)
  }
}

/** 5-min cache helper */
export function getCachedLocation(): Omit<CachedLocation,'timestamp'> | null {
  try {
    const raw = safeGetItem(LOCATION_CACHE_KEY);
    if (!raw) return null;
    const p: CachedLocation = JSON.parse(raw);
    if (Date.now() - p.timestamp < LOCATION_CACHE_DURATION) {
      return { latitude: p.latitude, longitude: p.longitude };
    }
    // expired
    try { localStorage.removeItem(LOCATION_CACHE_KEY); } catch {}
  } catch {
    // ignore parse/storage errors
  }
  return null;
}

function setCachedLocation(coords: { latitude: number; longitude: number }) {
  try {
    safeSetItem(
      LOCATION_CACHE_KEY,
      JSON.stringify({ ...coords, timestamp: Date.now() })
    );
  } catch { /* ignore */ }
}

/**
 * 1) Onboard/native (via NativeLocation → localStorage)
 * 2) 5-min cache
 * 3) Browser geolocation **only on non-iOS** desktop
 * 4) Default
 */
export async function getUserLocation(): Promise<{
  latitude: number;
  longitude: number;
  permission: 'granted' | 'denied' | 'unknown';
}> {
  const respond = (
    loc: { latitude: number; longitude: number },
    perm: 'granted' | 'denied' | 'unknown'
  ) => ({ ...loc, permission: perm });

  // 1) Onboard/native-injected
  try {
    const rawOn = safeGetItem(LOCATION_ONBOARD_KEY);
    if (rawOn) {
      const { latitude, longitude } = JSON.parse(rawOn);
      if (typeof latitude === 'number' && typeof longitude === 'number') {
        const perm = (safeGetItem(LOCATION_PERMISSION_KEY) as any) || 'granted';
        console.log('✅ Using onboard/native location');
        return respond({ latitude, longitude }, perm);
      }
    }
  } catch {
    console.warn('❌ Bad onboarding JSON');
  }

  // 2) 5-minute cache
  const cached = getCachedLocation();
  if (cached) {
    const perm = (safeGetItem(LOCATION_PERMISSION_KEY) as any) || 'unknown';
    console.log('✅ Using cached location');
    return respond(cached, perm);
  }

  // 3) Browser geolocation → **SKIP** on iOS WKWebView
  const ua = navigator.userAgent || '';
  const isIOS = /iP(hone|od|ad)/.test(ua);
  if (!isIOS && 'geolocation' in navigator) {
    try {
      const pos: GeolocationPosition = await new Promise((resolve, reject) => {
        const t = setTimeout(() => reject(new Error('timeout')), 5000);
        navigator.geolocation.getCurrentPosition(
          p => { clearTimeout(t); resolve(p); },
          e => { clearTimeout(t); reject(e); },
          { timeout: 5000, maximumAge: 300_000 }
        );
      });
      const coords = {
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude
      };
      setCachedLocation(coords);
      safeSetItem(LOCATION_PERMISSION_KEY, 'granted');
      console.log('📍 Browser geolocation granted', coords);
      return respond(coords, 'granted');
    } catch (err: any) {
      console.warn('⚠️ Browser geolocation failed:', err);
      safeSetItem(LOCATION_PERMISSION_KEY, 'denied');
    }
  }

  // 4) Default
  console.warn('⚠️ Falling back to default location');
  return respond(DEFAULT_LOCATION, 'unknown');
}
